// ─── LottiePicker ─────────────────────────────────────────────────────────
// Buscador + grilla de animaciones Lottie. Se monta dentro del panel
// "Elementos → Animación". Cuando el usuario clickea un tile dispara
// `onPick(item)` con la animación elegida; el caller inserta un clip con
// el template `lottie-asset-v1` apuntando a `item.url`.
//
// El listado viene de `searchLotties()` (ver `lottieLibrary.ts`). Cada
// tile renderiza la animación en miniatura usando `DotLottieReact`. Para
// no consumir CPU con animaciones que no se ven, observamos el viewport
// con `IntersectionObserver` y montamos el reproductor solo cuando el
// tile está visible.

import React, { useEffect, useRef, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Search, AlertCircle } from 'lucide-react';
import { searchLotties, type LottieItem } from './lottieLibrary';

type Props = {
  onPick: (item: LottieItem) => void;
};

export const LottiePicker: React.FC<Props> = ({ onPick }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<LottieItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Búsqueda con un debounce mínimo. La implementación actual es local
  // así que es instantánea, pero dejamos el patrón listo por si después
  // se cambia por una API remota.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      searchLotties(query).then(res => {
        if (!cancelled) {
          setItems(res);
          setLoading(false);
        }
      });
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  return (
    <div className="space-y-3" data-clip-ui="true">
      {/* Buscador */}
      <div className="relative">
        <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Buscar animación…"
          className="w-full h-10 pl-9 pr-3 rounded-md border border-black/[0.08] bg-white text-[14px] text-brand-black/85 placeholder:text-neutral-400 focus:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/15 transition-colors"
        />
      </div>

      {/* Resultados */}
      {loading ? (
        <p className="text-[13px] text-neutral-400 px-1 py-3">Buscando…</p>
      ) : items.length === 0 ? (
        <p className="text-[13px] text-neutral-500 px-1 py-3">
          No encontramos animaciones para "{query}". Probá con otra palabra.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {items.map(item => (
            <LottieTile
              key={item.id}
              item={item}
              onClick={async () => {
                // Bajamos el JSON para leer la duración natural (op/fr).
                // El navegador ya tiene esto cacheado del preview, así
                // que la mayoría de las veces es instantáneo.
                let durationSec: number | undefined;
                try {
                  const json = await fetch(item.url).then(r => r.json());
                  const op = Number(json?.op);
                  const ip = Number(json?.ip ?? 0);
                  const fr = Number(json?.fr);
                  if (Number.isFinite(op) && Number.isFinite(fr) && fr > 0) {
                    durationSec = (op - ip) / fr;
                  }
                } catch {
                  // Si falla, dejamos durationSec undefined y el editor
                  // usa el default del template.
                }
                onPick({ ...item, durationSec });
              }}
            />
          ))}
        </div>
      )}

    </div>
  );
};

// ── Tile individual ────────────────────────────────────────────────────────
// Tres estados visuales: cargando (pulso gris), listo (lottie animado),
// error (alerta roja). Sin esto las animaciones que fallan en el CDN se
// veían simplemente como cuadrados vacíos.
type TileProps = { item: LottieItem; onClick: () => void };
type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const LottieTile: React.FC<TileProps> = ({ item, onClick }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<LoadState>('loading');

  // Pausa cuando sale del viewport — montar muchos lotties simultáneos
  // es caro.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Red de seguridad: si el reproductor no dispara `load` ni `loadError`
  // en un tiempo razonable, asumimos que el recurso falló (CDN colgado,
  // CORS bloqueando, etc.) y mostramos el estado de error.
  useEffect(() => {
    if (!visible || state !== 'loading') return;
    const t = setTimeout(() => {
      setState(s => (s === 'loading' ? 'error' : s));
    }, 8000);
    return () => clearTimeout(t);
  }, [visible, state]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={state === 'error' ? undefined : onClick}
      disabled={state === 'error'}
      title={state === 'error' ? `${item.title} — no se pudo cargar` : item.title}
      aria-label={item.title}
      className={`group w-full text-left rounded-md bg-white border shadow-sm transition-all overflow-hidden ${
        state === 'error'
          ? 'border-red-200 cursor-not-allowed opacity-70'
          : 'border-black/[0.06] hover:border-brand-primary/30 hover:shadow-md hover:-translate-y-px'
      }`}
    >
      <div className="relative w-full" style={{ paddingTop: '100%' }}>
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-50">
          {/* El reproductor se monta cuando el tile entra en viewport.
              Lo dejamos siempre visible (opacity 0 mientras carga) — los
              estados loading/error van encima como overlay. Usamos los
              eventos `load` y `loadError` del player como fuente de
              verdad del estado del recurso. */}
          {visible && (
            <DotLottieReact
              src={item.url}
              autoplay
              loop
              dotLottieRefCallback={(d) => {
                if (!d) return;
                // `addEventListener` puede fallar en versiones viejas;
                // por eso lo envolvemos en try/catch defensivo.
                try {
                  d.addEventListener('load', () => setState('ready'));
                  d.addEventListener('loadError', () => setState('error'));
                } catch {
                  // Si los eventos no están disponibles el timeout
                  // termina decidiendo.
                }
              }}
              style={{
                width: '85%', height: '85%',
                opacity: state === 'ready' ? 1 : 0,
                transition: 'opacity 150ms ease',
              }}
            />
          )}
          {state === 'loading' && (
            <div className="absolute w-8 h-8 rounded-full border-2 border-neutral-200 border-t-neutral-400 animate-spin" />
          )}
          {state === 'error' && (
            <div className="absolute flex flex-col items-center gap-1 text-red-400 px-2 text-center">
              <AlertCircle className="w-5 h-5" />
              <span className="text-[10px] font-medium leading-tight">No disponible</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
